import { IQuestion, QuestionType } from '@quizzach/interfaces';

// ------------------------------------------------------------------ //
// Frontend-only content-pattern detection for standard play slides.
// The play payload is the single source: question HTML and answer
// HTML. Rules are literal markers checked in priority order; the
// first confident match picks a layout and anything unsure stays on
// the standard slide, which is never wrong.
// No backend or database involvement by design (founder decision,
// 2026-08-26 — see quizzach-question-pattern-proposals.html).
// ------------------------------------------------------------------ //

export type ContentPattern =
	| {
			kind: 'lyrics';
			verseHtml: string;
			questionHtml: string;
			/** prose above a mid-text verse block; the verse renders between
			 *  intro and questionHtml so the reading order stays intact */
			introHtml?: string;
	  }
	| {
			kind: 'quote';
			quoteHtml: string;
			questionHtml: string;
			/** the ask preceding an end-positioned quote (»Tko je rekao:
			 *  "…"«) — renders ABOVE the quote so the reading order of the
			 *  original text is never changed */
			introHtml?: string;
	  }
	| {
			kind: 'yearEvents';
			introHtml: string;
			events: string[];
			yearAnswer: string | null;
			/** prose that followed the list — renders below the timeline so
			 *  the original reading order is kept */
			outroHtml: string;
	  }
	| {
			kind: 'subParts';
			introHtml: string;
			parts: Array<{ label: string; html: string }>;
			answerParts: Array<{ label: string; html: string }> | null;
			/** trailing »Konačan odgovor: …« line after the lettered answers */
			answerTail: { label: string | null; html: string } | null;
	  }
	| {
			kind: 'numberedList';
			introHtml: string;
			items: Array<{ label: string; html: string }>;
			outroHtml: string;
			answerParts: Array<{ label: string; html: string }> | null;
	  }
	| {
			kind: 'labeledFacts';
			introHtml: string;
			facts: Array<{ label: string; html: string }>;
			outroHtml: string;
	  }
	| { kind: 'anagram'; introHtml: string; scramble: string }
	| {
			kind: 'ordering';
			questionHtml: string;
			items: string[];
			answerItems: string[] | null;
	  }
	| { kind: 'link'; clues: string[]; questionHtml: string }
	| { kind: 'handList'; items: string[]; questionHtml: string }
	| {
			kind: 'recipe';
			/** the ask preceding the recipe — renders above the card */
			introHtml: string;
			ingredients: string[];
			/** empty for ingredients-only recipes (»Sastojci:« with no prep) */
			prepHtml: string;
			/** the ask closing the text (»Ovo je recept za koji koktel?«) —
			 *  renders below the card, in its original place */
			outroHtml: string;
	  };

export interface StandardQuestionAnalysis {
	/** author instruction extracted from a trailing »Napomena: …« line */
	note: string | null;
	/** opening call-to-arms (»Riješi ovaj zadatak«, »Tko sam ja?«) lifted
	 *  into the handwritten gold kick above the question */
	kicker: string | null;
	/** the question HTML with the napomena line removed */
	questionHtml: string;
	pattern: ContentPattern | null;
}

const stripTags = (html: string): string =>
	html
		.replace(/<[^>]+>/g, ' ')
		.replace(/&nbsp;/gi, ' ')
		.replace(/&#39;|&apos;/gi, "'")
		.replace(/&quot;/gi, '"')
		.replace(/&amp;/gi, '&')
		.replace(/\s+/g, ' ')
		.trim();

/** trailing/leading <br> runs, empty paragraphs and stray quote marks */
const trimEdges = (html: string): string =>
	html
		.replace(/^(?:\s|<br\s*\/?>|<p>\s*<\/p>|["„“»«])+/i, '')
		.replace(/(?:\s|<br\s*\/?>|<p>\s*<\/p>|["„“”»«])+$/i, '');

const trimQuotes = (html: string): string =>
	html
		.replace(/^\s*["„“»«]+/, '')
		.replace(/["“”»«]+\s*$/, '')
		.trim();

const LINE_SPLIT = /<br\s*\/?>|<\/p>|\n/i;

/** italic runs split only by a fill-in blank (»</i>___<i>«, ellipses)
 *  are one quoted voice — merge them so the whole dialogue lifts as a
 *  single verse instead of cutting at the first blank. Breaks are not
 *  glue: two italic blocks on separate lines stay separate. */
const mergeItalicRuns = (html: string): string =>
	html.replace(/<\/(?:i|em)>((?:[ \t]|_+|…|\.{3})+)<(?:i|em)[^>]*>/gi, '$1');

// ---------------------------------------------------------------- //
// Napomena — an author instruction, not question content. It always
// trails the question; every pattern rule runs on the html without it
// so cursive notes are never mistaken for lyrics or quotes.
// ---------------------------------------------------------------- //
const extractNote = (html: string): { note: string | null; html: string } => {
	// whole word only — »Napomenama se ne bavimo …« is question prose
	const markers = [...html.matchAll(/napomena\b/gi)];
	if (markers.length === 0) return { note: null, html };
	const idx = markers[markers.length - 1].index!;

	// the marker must start its own line (start of text or after a break /
	// opening tag) and live in the tail of the question
	const before = html.slice(0, idx);
	if (!/(?:^|<br\s*\/?>|<\/p>|\n)\s*(?:<[^>]+>\s*)*$/i.test(before)) {
		return { note: null, html };
	}
	// the note trails the question: there must be question text before it
	// and no ask after it — a »?« in the tail means the marker sits inside
	// the question, not in a closing napomena line
	if (!stripTags(before)) return { note: null, html };
	if (stripTags(html.slice(idx)).includes('?')) return { note: null, html };

	const note = stripTags(html.slice(idx))
		.replace(/^napomena\s*:?\s*/i, '')
		.trim();
	if (!note) return { note: null, html };

	// drop an opening tag left dangling by the cut (e.g. the note's <i>)
	const rest = trimEdges(before.replace(/<(i|em|b|strong)[^>]*>\s*$/i, ''));
	return { note, html: rest };
};

// ---------------------------------------------------------------- //
// Kicker — an opening call-to-arms the author uses as a hook, not
// question content. Only the literal phrases below qualify, and each
// must close cleanly (punctuation or a break for the imperatives, the
// »?« for »Tko sam ja?«) so a longer sentence (»Riješi ovaj zadatak i
// osvoji bod …«) is never cut in half. Pattern rules run on the html
// without it, and it renders as the handwritten gold kick above the
// question.
// ---------------------------------------------------------------- //
const KICKER_RE =
	/^\s*(?:<[^>]+>\s*)*(?:(Riješi(?:te)?\s+ov(?:aj\s+zadatak|u\s+mozgalicu))(?:\s*<\/[^>]+>)*\s*(?:[:.!]|<br\s*\/?>)|(Tko\s+sam\s+ja\s*\?)(?:\s*<\/[^>]+>)*)/i;

const extractKicker = (
	html: string
): { kicker: string | null; html: string } => {
	const m = html.match(KICKER_RE);
	if (!m) return { kicker: null, html };

	// drop closing tags whose opener the cut consumed (»<b>Riješi ovaj
	// zadatak:</b> …«) and any punctuation left at the seam
	const rest = trimEdges(
		html
			.slice(m[0].length)
			.replace(/^\s*(?:<\/[^>]+>\s*)*/, '')
			.replace(/^[\s:.!]+/, '')
	);
	// a kicker with nothing after it is the whole question — stay standard
	if (!stripTags(rest)) return { kicker: null, html };

	return { kicker: (m[1] ?? m[2]).replace(/\s+/g, ' '), html: rest };
};

// ---------------------------------------------------------------- //
// individual pattern rules — each returns null when not confident
// ---------------------------------------------------------------- //

/** »Sastojci:« / »Priprema:« block labels; bolded labels may close
 *  their tag before the colon (»<b>Sastojci</b>:«) */
const SASTOJCI_RE = /Sastojci\s*(?:<\/[^>]+>\s*)*:/i;
const PRIPREMA_RE = /Priprema\s*(?:<\/[^>]+>\s*)*:/i;

/** the marker must start its own line — »…njegovi osnovni sastojci:
 *  patlidžan, …« mid-sentence is question prose, not a block label */
const lineAnchored = (html: string, idx: number): boolean =>
	/(?:^|<br\s*\/?>|<\/p>|<\/ul>|\n)\s*(?:<[^>]+>\s*)*$/i.test(
		html.slice(0, idx)
	);

const findMarker = (
	html: string,
	re: RegExp
): { start: number; end: number } | null => {
	for (const m of html.matchAll(new RegExp(re.source, 'gi'))) {
		if (lineAnchored(html, m.index!))
			return { start: m.index!, end: m.index! + m[0].length };
	}
	return null;
};

/**
 * Recipes are recognized ONLY by the literal »Sastojci:« / »Priprema:«
 * line labels (founder decision, 2026-08-27) — quantity-shaped lines on
 * their own stay standard, so »3 Doors Down«-style enumerations can
 * never dress up as a recipe card. Either marker alone is enough:
 * cocktail recipes list the ingredients bare and only label the
 * »Priprema:« block. The reading order is kept — the ask renders above
 * the card when it precedes the recipe and below it when it closes the
 * text.
 */
const matchRecipe = (html: string): ContentPattern | null => {
	const s = findMarker(html, SASTOJCI_RE);
	const p = findMarker(html, PRIPREMA_RE);
	if (!s && !p) return null;
	if (s && p && p.start <= s.start) return null;

	let introHtml: string;
	let ingBlock: string;
	const prepBlock = p ? html.slice(p.end) : '';

	if (s) {
		introHtml = html.slice(0, s.start);
		ingBlock = html.slice(s.end, p ? p.start : html.length);
	} else {
		// »Priprema:« only — the ingredients are the <ul> or the
		// quantity-run above the marker, prose above them is the ask
		const before = html.slice(0, p!.start);
		const ul = before.match(/<ul[\s\S]*?<\/ul>/i);
		if (ul) {
			if ((before.match(/<ul[\s>]/gi) || []).length > 1) return null;
			// prose between the list and the marker has no slot that keeps
			// the original order — stay standard
			if (stripTags(before.slice(ul.index! + ul[0].length))) return null;
			introHtml = before.slice(0, ul.index!);
			ingBlock = ul[0];
		} else {
			const lines = before
				.split(LINE_SPLIT)
				.map((line) => ({ line, text: stripTags(line) }))
				.filter((l) => l.text);
			const first = lines.findIndex((l) => startsWithAmount(l.text));
			if (first === -1) return null;
			// everything from the first amount line to the marker must look
			// like an ingredient — bare items (»sol i papar«) ride along
			if (
				!lines
					.slice(first)
					.every(
						(l) =>
							startsWithAmount(l.text) ||
							(l.text.length <= 60 && !l.text.includes('?'))
					)
			) {
				return null;
			}
			introHtml = lines
				.slice(0, first)
				.map((l) => l.line)
				.join('<br />');
			ingBlock = lines
				.slice(first)
				.map((l) => l.line)
				.join('<br />');
		}
	}

	const ingredients = (
		/<li[\s>]/i.test(ingBlock)
			? [...ingBlock.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)].map((m) =>
					stripTags(m[1])
				)
			: ingBlock.split(LINE_SPLIT).map((l) => stripTags(l))
	).filter(Boolean);
	if (ingredients.length < 2) return null;
	if (!ingredients.every((it) => it.length <= 80 && !it.includes('?')))
		return null;

	// the ask closing the text sits at the end of the prep block —
	// trailing »?« lines split off and render below the card
	const prepLines = prepBlock.split(LINE_SPLIT);
	let firstAsk = prepLines.length;
	while (firstAsk > 0) {
		const text = stripTags(prepLines[firstAsk - 1]);
		if (text && !text.includes('?')) break;
		firstAsk--;
	}
	const prepHtml = trimEdges(prepLines.slice(0, firstAsk).join('<br />'));
	const outroHtml = trimEdges(prepLines.slice(firstAsk).join('<br />'));

	// the cut can leave the label's opening tag (»<b>Sastojci</b>:«)
	// dangling at the end of the intro — drop it
	introHtml = trimEdges(introHtml.replace(/<(?:i|em|b|strong)[^>]*>\s*$/i, ''));
	// the ask has to live somewhere around the card
	if (!stripTags(introHtml) && !stripTags(outroHtml)) return null;

	return { kind: 'recipe', introHtml, ingredients, prepHtml, outroHtml };
};

// ---------------------------------------------------------------- //
// Ingredient quantities — shared by detection (below) and the
// mise-en-place split the recipe card renders with.
// ---------------------------------------------------------------- //

const AMOUNT_UNITS = new Set([
	'g',
	'dag',
	'dkg',
	'kg',
	'ml',
	'dl',
	'dcl',
	'l',
	'litra',
	'litre',
	'kom',
	'žlica',
	'žlice',
	'žličica',
	'žličice',
	'šalica',
	'šalice',
	'prstohvat',
	'češnja',
	'češnjeva',
	'kap',
	'kapi',
]);

/** word-quantities that open an ingredient line without a digit */
const AMOUNT_WORD =
	/^(prstohvat|nekoliko|malo|pola|par|šaka|šačica|žlica|žlice|žličica|žličice|šalica|šalice)\b/i;

/** »140 g«, »12.5 dl«, »1/2«, »2–3« — but never a 4-digit year, so
 *  event lists (»1912. potonuo je …«) stay on their own pattern */
const AMOUNT_NUMERIC =
	/^\d{1,3}(?:[.,/]\d+)?(?:\s*([–—-])\s*\d[\d.,/]*)?\s+(\S+)/;

const startsWithAmount = (text: string): boolean => {
	if (AMOUNT_WORD.test(text)) return true;
	const m = text.match(AMOUNT_NUMERIC);
	if (!m) return false;
	// a dash range counts as an amount only with a real unit behind it —
	// match results (»3-0 protiv Argentine«) are not ingredients
	return !m[1] || AMOUNT_UNITS.has(m[2].toLowerCase());
};

/* Mise-en-place split: the leading quantity (digits or a word amount,
   plus a known unit) lands in the muted column, the ingredient itself
   stays in ink. Lines with no quantity get an em-dash placeholder. */
export const splitIngredient = (
	item: string
): { amt: string; name: string } => {
	const m = item.match(
		/^((?:\d[\d.,/]*)(?:\s*[–—-]\s*\d[\d.,/]*)?|prstohvat|nekoliko|malo|pola|par|šaka|šačica|žlica|žlice|žličica|žličice|šalica|šalice)\s+(.*)$/i
	);
	if (!m) return { amt: '—', name: item };

	let amt = m[1];
	let rest = m[2];
	const words = rest.split(/\s+/);
	if (words.length > 1 && AMOUNT_UNITS.has(words[0].toLowerCase())) {
		amt = `${amt} ${words[0]}`;
		rest = words.slice(1).join(' ');
	}
	return { amt, name: rest || item };
};

/** A) … B) … multi-part sub-questions (letters a–j in order);
 *  the separator after the letter is ) : or a dash (»A – …«) — never
 *  a dot, which belongs to name initials (»A. Hitchcock je …«), and a
 *  dash only after whitespace, so compounds (»a-moll«, »E-mail«) never
 *  read as labels. Authors bold the letters (»<b>A</b> – …«, gold in
 *  play), so closing tags may sit between the letter and its separator
 *  — or right after. */
const PART_LINE =
	/^\s*(?:<[^>]+>\s*)*([A-Ja-j])(?:\s*(?:<\/[^>]+>\s*)*[):]|(?:\s|<\/[^>]+>|&nbsp;)+[–—-])\s*(?:<\/[^>]+>\s*)*(.+?)\s*$/;

const parseParts = (
	html: string
): {
	intro: string[];
	parts: Array<{ label: string; html: string }>;
	outro: string[];
} => {
	const intro: string[] = [];
	const parts: Array<{ label: string; html: string }> = [];
	const outro: string[] = [];
	for (const line of html.split(LINE_SPLIT)) {
		const m = line.match(PART_LINE);
		if (m) {
			parts.push({ label: m[1].toLowerCase(), html: m[2] });
		} else if (parts.length === 0) {
			intro.push(line);
		} else if (stripTags(line)) {
			outro.push(line);
		}
	}
	return { intro, parts, outro };
};

const matchSubParts = (
	html: string,
	answerHtml: string
): ContentPattern | null => {
	const { intro, parts, outro } = parseParts(html);
	if (parts.length < 2) return null;
	// prose after the lettered rows has nowhere to render — stay standard
	if (outro.length > 0) return null;

	// letters must run a, b, c… from the top — anything else is not the shape
	const alphabet = 'abcdefghij';
	if (!parts.every((p, i) => p.label === alphabet[i])) return null;

	// the rows may BE the whole question (»A – four players« match-up
	// lists, the ask living in the points explanation) — the intro may
	// stay empty; the a-then-b sequence and the dot-free separator are
	// what makes the shape confident
	const introHtml = trimEdges(intro.join('<br />'));

	const answer = parseParts(answerHtml);
	// prose before the lettered answers (»Točni odgovori redom: …«) has
	// no slot in the pair rendering — fall back to the raw answer
	const answerParts =
		answer.parts.length === parts.length &&
		answer.intro.every((l) => !stripTags(l)) &&
		answer.parts.every((p, i) => p.label === alphabet[i])
			? answer.parts
			: null;

	// a trailing »Konačan odgovor: …« after the lettered answers keeps
	// the same language: its label renders like the letters do
	let answerTail: { label: string | null; html: string } | null = null;
	if (answerParts && answer.outro.length > 0) {
		const tail = answer.outro.join(' ');
		const idx = colonOutsideTags(tail);
		answerTail =
			idx > 0
				? {
						label: stripTags(tail.slice(0, idx)).toLowerCase(),
						html: tail.slice(idx + 1).trim(),
					}
				: { label: null, html: tail };
	}

	return { kind: 'subParts', introHtml, parts, answerParts, answerTail };
};

/**
 * 1. … 2. … numbered lines (also 1) …) — the digits become the same
 * number indicators the connection columns use. The question text may
 * sit before the list, after it (»Koji smo grijeh izostavili?«), or
 * both; numbered lines interleaved with prose are not the shape.
 */
const NUM_LINE = /^\s*(?:<[^>]+>\s*)*(\d{1,2})\s*([.)])\s*(.+?)\s*$/;

/** »1. siječnja …« opens a date, not a list row */
const MONTH_AFTER_DOT =
	/^(siječnja|veljače|ožujka|travnja|svibnja|lipnja|srpnja|kolovoza|rujna|listopada|studenog(a)?|prosinca)\b/i;

/** »1. 9. 1939. …« and »1. 1939. …« open numeric dates — another
 *  day/month ordinal or a 4-digit year right after the dot means the
 *  digits count a calendar, not a list */
const DATE_AFTER_DOT = /^(?:\d{1,2}\.(?:\s|$)|[12]\d{3}\b)/;

const parseNumbered = (
	html: string
): {
	intro: string[];
	items: Array<{ label: string; html: string }>;
	outro: string[];
} | null => {
	const intro: string[] = [];
	const items: Array<{ label: string; html: string }> = [];
	const outro: string[] = [];
	for (const line of html.split(LINE_SPLIT)) {
		const m = line.match(NUM_LINE);
		const rest = m ? stripTags(m[3]) : '';
		if (
			m &&
			!(
				m[2] === '.' &&
				(MONTH_AFTER_DOT.test(rest) || DATE_AFTER_DOT.test(rest))
			)
		) {
			// a numbered line after the closing prose = not confident
			if (outro.length > 0) return null;
			items.push({ label: m[1], html: m[3] });
		} else if (items.length === 0) {
			intro.push(line);
		} else if (stripTags(line)) {
			outro.push(line);
		}
	}
	return { intro, items, outro };
};

const matchNumberedList = (
	html: string,
	answerHtml: string
): ContentPattern | null => {
	const parsed = parseNumbered(html);
	if (!parsed || parsed.items.length < 2) return null;

	// digits must count 1, 2, 3… from the top — anything else (years,
	// dates, stray measurements) is not a list
	if (!parsed.items.every((it, i) => Number(it.label) === i + 1)) return null;
	if (!parsed.items.every((it) => stripTags(it.html))) return null;

	const introHtml = trimEdges(parsed.intro.join('<br />'));
	const outroHtml = trimEdges(parsed.outro.join('<br />'));
	// the question itself has to live somewhere around the list
	if (!stripTags(introHtml) && !stripTags(outroHtml)) return null;

	const answer = parseNumbered(answerHtml);
	// prose around the numbered answers has no slot in the pair
	// rendering — fall back to the raw answer instead of dropping it
	const answerParts =
		answer &&
		answer.items.length === parsed.items.length &&
		answer.intro.every((l) => !stripTags(l)) &&
		answer.outro.length === 0 &&
		answer.items.every((it, i) => Number(it.label) === i + 1)
			? answer.items
			: null;

	return {
		kind: 'numberedList',
		introHtml,
		items: parsed.items,
		outroHtml,
		answerParts,
	};
};

/**
 * »Etiketa: činjenica« dossier rows (Simptomi, Prijenos, Inkubacija…)
 * — krug 7 GO: dosje-mreža. A label is 1–3 plain words before a colon
 * at the start of its line; the question may sit before and/or after
 * the rows. Sastojci/Priprema belong to the recipe pattern and a
 * digit in the label (times, years) disqualifies the line.
 */
const FACT_LABEL_EXCLUDE = /^(sastojci|priprema|napomena)$/i;

const colonOutsideTags = (s: string): number => {
	let inTag = false;
	for (let i = 0; i < s.length; i++) {
		if (s[i] === '<') inTag = true;
		else if (s[i] === '>') inTag = false;
		else if (s[i] === ':' && !inTag) return i;
	}
	return -1;
};

const factFromLine = (line: string): { label: string; html: string } | null => {
	const m = stripTags(line).match(/^([^:?!.]{2,30}):\s*(.+)$/);
	if (!m) return null;

	const label = m[1].trim();
	if (label.split(/\s+/).length > 3) return null;
	if (/\d/.test(label) || FACT_LABEL_EXCLUDE.test(label)) return null;

	const idx = colonOutsideTags(line);
	if (idx === -1) return null;
	const html = line
		.slice(idx + 1)
		.replace(/^\s*<\/(?:b|strong|i|em|u)>/i, '')
		.trim();
	if (!stripTags(html)) return null;

	return { label, html };
};

const matchLabeledFacts = (html: string): ContentPattern | null => {
	const intro: string[] = [];
	const facts: Array<{ label: string; html: string }> = [];
	const outro: string[] = [];
	for (const line of html.split(LINE_SPLIT)) {
		const fact = factFromLine(line);
		if (fact) {
			// a fact line after the closing prose = not confident
			if (outro.length > 0) return null;
			facts.push(fact);
		} else if (facts.length === 0) {
			intro.push(line);
		} else if (stripTags(line)) {
			outro.push(line);
		}
	}
	if (facts.length < 2) return null;

	const introHtml = trimEdges(intro.join('<br />'));
	const outroHtml = trimEdges(outro.join('<br />'));
	// the question itself has to live somewhere around the rows
	if (!stripTags(introHtml) && !stripTags(outroHtml)) return null;

	// speaker-labelled dialogue (»Ilsa: Play it, Sam.«) reads as lines
	// of speech, not dossier rows — the surrounding ask names it
	if (
		/dijalog|razgovor|replik|citat/i.test(
			stripTags(introHtml + ' ' + outroHtml)
		)
	)
		return null;

	return { kind: 'labeledFacts', introHtml, facts, outroHtml };
};

/** intro sentence + <ul><li> clue list (the »u kojoj godini« family) */
const matchYearEvents = (
	html: string,
	answerHtml: string
): ContentPattern | null => {
	// two lists would merge and swallow the prose between them — one
	// <ul> is the only confident shape
	if ((html.match(/<ul[\s>]/gi) || []).length !== 1 || !/<li[\s>]/i.test(html))
		return null;
	const ul = html.match(/<ul[\s\S]*?<\/ul>/i)!;

	const events = [...ul[0].matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
		.map((m) => stripTags(m[1]))
		.filter(Boolean);
	if (events.length < 2) return null;

	// a list of quantities is an unlabeled recipe, not a timeline — with
	// no »Sastojci:« marker it stays on the standard slide
	if (events.filter((e) => startsWithAmount(e)).length * 2 > events.length)
		return null;

	// prose keeps its place: text before the list renders above the
	// timeline, text after it below — the reading order never changes
	const introHtml = trimEdges(html.slice(0, ul.index!));
	const outroHtml = trimEdges(html.slice(ul.index! + ul[0].length));
	if (!stripTags(introHtml) && !stripTags(outroHtml)) return null;

	const answerText = stripTags(answerHtml);
	const yearAnswer = /^[12]\d{3}\.?$/.test(answerText) ? answerText : null;

	return { kind: 'yearEvents', introHtml, events, yearAnswer, outroHtml };
};

/** »Riješite anagram …« + the bolded scramble */
const matchAnagram = (html: string): ContentPattern | null => {
	if (!/anagram/i.test(stripTags(html))) return null;
	const bold = html.match(/<(?:b|strong)[^>]*>([\s\S]*?)<\/(?:b|strong)>/i);
	if (!bold) return null;

	const scramble = stripTags(bold[1]);
	if (!scramble || scramble.length > 60) return null;

	const introHtml = trimEdges(html.replace(bold[0], ''));
	if (!stripTags(introHtml)) return null;

	return { kind: 'anagram', introHtml, scramble };
};

/** »Poredajte …: x, y, z« ordering tasks — answer joins with arrows */
const matchOrdering = (
	html: string,
	answerHtml: string
): ContentPattern | null => {
	if (!/poredaj/i.test(stripTags(html))) return null;

	// answers join with arrows, so every step has to be list-shaped —
	// trailing prose (»…, a zanimljivo je da …«) falls back to the raw answer
	const answerSteps = stripTags(answerHtml)
		.split(',')
		.map((s) => s.trim().replace(/\.$/, ''))
		.filter(Boolean);
	const answerItems =
		answerSteps.length >= 2 && answerSteps.every((s) => s.length <= 30)
			? answerSteps
			: null;

	// members enumerated after a colon become glass pills
	const colon = html.indexOf(':');
	let items: string[] = [];
	let questionHtml = html;
	if (colon !== -1) {
		const tail = stripTags(html.slice(colon + 1)).replace(/\.\s*$/, '');
		const candidates = tail.split(',').map((s) => s.trim());
		if (
			candidates.length >= 3 &&
			candidates.every((c) => c.length > 0 && c.length <= 24)
		) {
			items = candidates;
			questionHtml = trimEdges(html.slice(0, colon + 1));
		}
	}

	// nothing parsed on either side = nothing to render differently —
	// stay out of the way so the quote/enumeration rules can still run
	if (items.length === 0 && !answerItems) return null;

	return { kind: 'ordering', questionHtml, items, answerItems };
};

/**
 * Quote marks OUTSIDE the italics — »"<i>Prijatelji</i> <i>ne</i>
 * <i>lažu</i>", ali …«, or a verse whose italic runs break around a
 * blank (»<i>"…long<br />the </i>_____<i> cat feeds…"</i>«). The marks,
 * not the tags, bound the quote: everything between them lifts as one
 * block. Confident only when the html holds exactly two marks, the
 * opening mark starts the question and italics carry the quoted voice;
 * anything else falls through to the single-italic rules below.
 */
const matchQuoteSpan = (html: string): ContentPattern | null => {
	const marks = [...html.matchAll(/["„“”»«]/g)];
	if (marks.length !== 2) return null;

	const openIdx = marks[0].index ?? 0;
	const closeIdx = marks[1].index ?? 0;
	if (stripTags(html.slice(0, openIdx))) return null;

	const span = html.slice(openIdx + 1, closeIdx);
	if (!/<\/?(?:i|em)[^>]*>/i.test(span)) return null;

	const text = stripTags(span);
	if (text.length < 4 || text.length > 800) return null;

	// the cut can leave a dangling </i> and the sentence's joining
	// punctuation (»…", ali tko …«) at the head of the question
	const questionHtml = trimEdges(
		html
			.slice(closeIdx + 1)
			.replace(/^\s*<\/(?:i|em|b|strong)>/i, '')
			.replace(/^[\s,;:.–—-]+/, '')
	);
	if (stripTags(questionHtml).length < 10) return null;

	const verse = trimEdges(span);
	return (span.match(/<br\s*\/?>/gi) || []).length >= 2
		? { kind: 'lyrics', verseHtml: verse, questionHtml }
		: { kind: 'quote', quoteHtml: verse, questionHtml };
};

/**
 * A lifted quote must sit at an edge of the text, and it renders WHERE
 * it sat — an opening quote stays above the ask, a closing quote stays
 * below it. The original reading order is never changed. Prose flowing
 * on BOTH sides means the italic is embedded mid-sentence (»Gibson mu
 * je rekao: <i>…</i> i osobno mu dao …«) — lifting it would leave a
 * broken sentence behind, so those stay on the standard slide.
 */
const atTextStart = (html: string, start: number): boolean =>
	/^["„“»«\s]*$/.test(stripTags(html.slice(0, start)));

const atTextEnd = (html: string, end: number): boolean =>
	/^["“”»«\s,;:.?!…–—-]*$/.test(stripTags(html.slice(end)));

/**
 * The ask preceding an end-positioned quote, with the sentence's
 * closing punctuation (»… izjavio: "…"?«) folded back onto it so the
 * question doesn't lose its »?« — a colon the quote used to follow
 * gives way to it (»izjavio:?« → »izjavio?«).
 */
const introBeforeQuote = (html: string, start: number, end: number): string => {
	const tailPunct = stripTags(html.slice(end)).replace(/["„“”»«\s]+/g, '');
	return trimEdges(`${trimEdges(html.slice(0, start))}${tailPunct}`).replace(
		/\s*[:,;]\s*([?!.])\s*$/,
		'$1'
	);
};

/** italic block with 2+ line breaks = quoted verse */
const matchLyrics = (html: string): ContentPattern | null => {
	for (const m of html.matchAll(/<(?:i|em)[^>]*>([\s\S]*?)<\/(?:i|em)>/gi)) {
		const inner = m[1];
		if ((inner.match(/<br\s*\/?>/gi) || []).length < 2) continue;
		if (/^\s*["„“»«]*\s*napomena/i.test(stripTags(inner))) continue;

		const start = m.index!;
		const end = start + m[0].length;

		// verse opens the text: verse, then the ask below — as written
		if (atTextStart(html, start)) {
			const questionHtml = trimEdges(html.slice(end));
			if (stripTags(questionHtml).length < 10) return null;
			return { kind: 'lyrics', verseHtml: trimQuotes(inner), questionHtml };
		}

		// verse closes the text: the ask stays above it — as written
		if (atTextEnd(html, end)) {
			const introHtml = introBeforeQuote(html, start, end);
			if (stripTags(introHtml).length < 10) return null;
			return {
				kind: 'lyrics',
				verseHtml: trimQuotes(inner),
				questionHtml: '',
				introHtml,
			};
		}

		// a standalone verse BLOCK mid-text — line breaks on both sides
		// with prose before (»…evo male pomoći:«) and a closing ask
		// after — lifts in place: intro, verse, ask keep their order.
		// Anything inline (no bounding breaks) is a genuinely embedded
		// italic and stays standard, same as before.
		const before = html.slice(0, start);
		const after = html.slice(end);
		if (!/(?:<br\s*\/?>|\n)\s*["„“»«]?\s*$/i.test(before)) continue;
		if (!/^\s*["„“»«]?\s*(?:<br\s*\/?>|\n)/i.test(after)) continue;

		const introHtml = trimEdges(before);
		const questionHtml = trimEdges(after);
		if (!stripTags(introHtml)) continue;
		if (stripTags(questionHtml).length < 10) continue;

		return {
			kind: 'lyrics',
			verseHtml: trimQuotes(inner),
			questionHtml,
			introHtml,
		};
	}
	return null;
};

/**
 * Italic quote = famous quote or excerpt. Two confident shapes:
 * an attribution word near the quote (»Tko je ovo izjavio?«,
 * »ulomak iz romana«), or a quote-marked italic that opens the
 * question — long literary excerpts carry no attribution verb, the
 * quotation marks themselves are the signal. A mid-sentence italic
 * without quote marks (a book or film title) stays standard.
 */
const ATTRIBUTION =
	/\b(rekao|rekla|kaže|kazao|kazala|govorio|govorila|izjavio|izjavila|uzviknuo|uzviknula|poručio|poručila|citat|ulomak|odlomak)\b/i;

const matchQuote = (html: string): ContentPattern | null => {
	// every italic gets a chance — a mid-text title (»U romanu <i>Zločin
	// i kazna</i> …«) must not shadow the real quote further down
	for (const m of html.matchAll(/<(?:i|em)[^>]*>([\s\S]*?)<\/(?:i|em)>/gi)) {
		const quote = stripTags(m[1]);
		if (quote.length < 4 || quote.length > 800) continue;
		if (/^["„“»«]*\s*napomena/i.test(quote)) continue;

		const start = m.index!;
		const end = start + m[0].length;

		// quote opens the text: quote, then the ask below — as written
		if (atTextStart(html, start)) {
			const questionHtml = trimEdges(
				html.slice(end).replace(/^[\s,;:.–—-]+/, '')
			);
			const questionText = stripTags(questionHtml);
			if (questionText.length < 10) continue;

			const lead = stripTags(html.slice(0, start)).trim();
			const opensQuoted = /^["„“»«]/.test((lead + quote).trim());
			if (!opensQuoted && !ATTRIBUTION.test(questionText)) continue;
			// more quote marks left in the question (»"X" i "Y" romani su …«)
			// read as an enumeration of titles, not a lifted excerpt
			if (!ATTRIBUTION.test(questionText) && /["„“”»«]/.test(questionText))
				continue;

			return { kind: 'quote', quoteHtml: trimQuotes(m[1]), questionHtml };
		}

		// quote closes the text: the ask stays above it — as written
		// (»Pep Guardiola: "…"«, »Tko je izjavio: "…"?«)
		if (atTextEnd(html, end)) {
			const introHtml = introBeforeQuote(html, start, end);
			const introText = stripTags(introHtml);
			if (introText.length < 10) continue;
			if (!ATTRIBUTION.test(introText)) continue;

			return {
				kind: 'quote',
				quoteHtml: trimQuotes(m[1]),
				questionHtml: '',
				introHtml,
			};
		}
	}
	return null;
};

/**
 * Comma enumeration + closing question. The closing wording decides
 * the flavour: »tražimo/povezuje/zajedničko« = link clues as glass
 * pills; otherwise short items (guitar brands, quoted book titles)
 * become the handwritten gold list. The closer may be a real question
 * (»…?«) or a statement-phrased ask (»… vjerujem da znate koji ih je
 * hrvatski književnik napisao.«) — an interrogative word stands in
 * for the missing question mark.
 */
/** quoted book/film titles enumerate wrapped (»"Ljubica", "Branka"«)
 *  — the list rendering carries the emphasis, so the marks come off;
 *  an item only partially quoted (»pjesma "Roklje"«) keeps its marks */
const unwrapQuotes = (s: string): string =>
	/^["„“»«].*["“”»«]$/.test(s) ? s.slice(1, -1).trim() : s;

const ASK_WORD =
	/\b(tko|što|koji|koja|koje|kojeg|kojo?j|kojim|kojem|gdje|kada|kako|čime|znate|prepoznaj\w*|pogodi\w*|pogađa\w*|imenuj\w*|navedi\w*|nabroj\w*)\b/i;

const enumerationAt = (text: string, cut: number): ContentPattern | null => {
	const questionHtml = text.slice(cut + 1).trim();
	if (questionHtml.length < 4 || questionHtml.length > 180) return null;
	if (!/\?$/.test(questionHtml) && !ASK_WORD.test(questionHtml)) return null;

	const lead = text.slice(0, cut + 1).trim();
	if (!lead || lead.includes('?')) return null;
	// enumerations read as bare noun phrases — a verby lead sentence is prose
	if (/\b(je|su|se|ima|nije|bio|bila)\b/i.test(lead)) return null;

	const items = lead
		.replace(/\.\s*$/, '')
		.split(',')
		.flatMap((part, i, arr) =>
			// »soda i izlomljeni led« — the final pair splits on » i «, but
			// only before a lowercase word: a capitalized continuation
			// (»Romeo i Julija«) is one compound title, not two items
			i === arr.length - 1 ? part.split(/\s+i\s+(?=[a-zčćžšđ])/) : [part]
		)
		.map((s) => unwrapQuotes(s.trim()))
		.filter(Boolean);

	if (items.length < 3) return null;
	if (!items.every((it) => it.length <= 45 && it.split(/\s+/).length <= 6))
		return null;

	if (/tražimo|povezuje|zajedničk/i.test(questionHtml)) {
		return { kind: 'link', clues: items, questionHtml };
	}
	if (items.every((it) => it.length <= 30)) {
		return { kind: 'handList', items, questionHtml };
	}
	return null;
};

const matchEnumeration = (html: string): ContentPattern | null => {
	// plain-prose shapes only — markup beyond breaks means something else
	if (/<(?!br\s*\/?>)[a-z]/i.test(html)) return null;

	const text = stripTags(html);

	// candidate seams between the enumeration and the closing question:
	// sentence breaks OUTSIDE parentheses followed by whitespace — the
	// ordinal-year dot in »Ruth Beitia (2016.)« and abbreviation dots
	// (»J.K. Rowling«) are not sentence ends
	let depth = 0;
	const seams: number[] = [];
	for (let i = 0; i < text.length - 1; i++) {
		const c = text[i];
		if (c === '(') depth++;
		else if (c === ')') depth = Math.max(0, depth - 1);
		else if (
			depth === 0 &&
			(c === '.' || c === '?' || c === '!') &&
			/\s/.test(text[i + 1])
		)
			seams.push(i);
	}
	// one unbroken sentence (»… Ruth Beitia (2016.) su bivše …?«): the
	// enumeration's last bracket is the seam — anything non-list shaped
	// still dies on the item checks and stays standard
	if (seams.length === 0) {
		const bracket = text.lastIndexOf(')');
		if (bracket !== -1) seams.push(bracket);
	}

	// earliest seam whose lead parses as a list wins, so a multi-sentence
	// closer (»Ovo možda nisu njegova najpoznatija djela, ali …«) stays
	// whole on the question side instead of being cut at its last period
	for (const cut of seams) {
		const match = enumerationAt(text, cut);
		if (match) return match;
	}
	return null;
};

// ---------------------------------------------------------------- //

export const analyzeStandardQuestion = (
	question: IQuestion
): StandardQuestionAnalysis => {
	if (
		question.type !== QuestionType.standard ||
		!question.standard_question?.question_hr
	) {
		return {
			note: null,
			kicker: null,
			questionHtml: question.standard_question?.question_hr || '',
			pattern: null,
		};
	}

	const { note, html: noteless } = extractNote(
		question.standard_question.question_hr
	);
	const { kicker, html: kickless } = extractKicker(noteless);
	const html = mergeItalicRuns(kickless);
	const answerHtml = question.standard_question.answer_hr || '';

	const pattern =
		matchRecipe(html) ??
		matchSubParts(html, answerHtml) ??
		matchNumberedList(html, answerHtml) ??
		matchLabeledFacts(html) ??
		matchYearEvents(html, answerHtml) ??
		matchAnagram(html) ??
		matchOrdering(html, answerHtml) ??
		matchQuoteFamily(html) ??
		matchEnumeration(html);

	return { note, kicker, questionHtml: html, pattern };
};

const matchQuoteFamily = (html: string): ContentPattern | null =>
	matchQuoteSpan(html) ?? matchLyrics(html) ?? matchQuote(html);

/**
 * ABCD questions get the quote/verse treatment too — the quoted
 * italic lifts above the question and the options render below
 * (»Tko je ovo izjavio?« + a/b/c). The list-shaped patterns stay
 * off; the options already own that space.
 */
export const analyzeAbcdQuestion = (
	question: IQuestion
): StandardQuestionAnalysis => {
	const raw = question?.abcd_question?.text || '';
	if (question?.type !== QuestionType.abcd || !raw) {
		return { note: null, kicker: null, questionHtml: raw, pattern: null };
	}

	const { note, html: noteless } = extractNote(raw);
	const { kicker, html: kickless } = extractKicker(noteless);
	const html = mergeItalicRuns(kickless);
	return { note, kicker, questionHtml: html, pattern: matchQuoteFamily(html) };
};

/**
 * Estimation and visual questions carry the same free-form prose as
 * standard ones, so the text-shaped patterns apply to them too — a/b/c
 * sums (»Zbrojite: a) …«), numbered lists, dossier rows, event lists,
 * anagrams, quotes and enumerations. Only the recipe card and the
 * ordering task stay standard-only.
 */
const analyzeProseQuestion = (
	raw: string,
	answerHtml: string
): StandardQuestionAnalysis => {
	const { note, html: noteless } = extractNote(raw);
	const { kicker, html: kickless } = extractKicker(noteless);
	const html = mergeItalicRuns(kickless);
	const pattern =
		matchSubParts(html, answerHtml) ??
		matchNumberedList(html, answerHtml) ??
		matchLabeledFacts(html) ??
		matchYearEvents(html, answerHtml) ??
		matchAnagram(html) ??
		matchQuoteFamily(html) ??
		matchEnumeration(html);
	return { note, kicker, questionHtml: html, pattern };
};

export const analyzeEstimationQuestion = (
	question: IQuestion
): StandardQuestionAnalysis => {
	const raw = question?.estimation_question?.text || '';
	if (question?.type !== QuestionType.estimation || !raw) {
		return { note: null, kicker: null, questionHtml: raw, pattern: null };
	}
	return analyzeProseQuestion(raw, question.estimation_question?.answer || '');
};

export const analyzeVisualQuestion = (
	question: IQuestion
): StandardQuestionAnalysis => {
	const raw = question?.visual_question?.text || '';
	if (question?.type !== QuestionType.visual || !raw) {
		return { note: null, kicker: null, questionHtml: raw, pattern: null };
	}
	return analyzeProseQuestion(raw, question.visual_question?.answer || '');
};

/**
 * Connection questions follow the ABCD rule: quote family only — the
 * letter/number columns below already own the list space.
 */
export const analyzeConnectionQuestion = (
	question: IQuestion
): StandardQuestionAnalysis => {
	const raw = question?.connection_question?.text || '';
	if (question?.type !== QuestionType.connection || !raw) {
		return { note: null, kicker: null, questionHtml: raw, pattern: null };
	}

	const { note, html: noteless } = extractNote(raw);
	const { kicker, html: kickless } = extractKicker(noteless);
	const html = mergeItalicRuns(kickless);
	return { note, kicker, questionHtml: html, pattern: matchQuoteFamily(html) };
};

/**
 * Napomena extraction for the question types without pattern
 * detection (visual, estimation, connection) — the same trailing-line
 * rule the standard/abcd analyses apply before pattern matching.
 */
export const extractQuestionNote = (
	html: string
): { note: string | null; questionHtml: string } => {
	const { note, html: questionHtml } = extractNote(html);
	return { note, questionHtml };
};

/** each letter/number + its answer travels as one .ap unit so narrow
 *  screens can stack the pairs one per line instead of ragged wraps;
 *  single-character labels get the glass-circle indicator (krug 7 GO:
 *  stakleni krug), word labels (»konačan odgovor«) stay plain text */
const pair = (label: string | null, html: string) =>
	`<span class="ap">${
		label
			? `<span class="al${label.length <= 2 ? ' alc' : ''}">${label}</span>`
			: ''
	}${html}</span>`;

/** leading letter label (»B)«, »c:«, »<b>B</b> –«) with any
 *  tags/entities around the letter and its separator. A dot is NOT a
 *  separator here — »C. Ronaldo« is an initial, not option c — and a
 *  dash needs whitespace before it, so »D-Day« stays a word. */
const leadingLabelRe =
	/^((?:\s|<[^>]+>|&nbsp;)*)([A-Ja-j])(?:(?:\s|<\/[^>]+>|&nbsp;)*[):]|(?:\s|<\/[^>]+>|&nbsp;)+[–—-])\s*(?:<\/[^>]+>\s*)*/;

/** the fragment with its leading letter label removed, inline markup
 *  (<sub>/<sup>/<strong>…) kept; null when the label isn't at the start
 *  of the raw HTML so the caller can fall back to the stripped text */
const bodyAfterLabel = (html: string): string | null => {
	const m = html.match(leadingLabelRe);
	return m ? html.slice(m[0].length) : null;
};

/**
 * A one-line answer made of lettered fragments (»B) X · C) Y · G) Z«,
 * separated by » · « or breaks) → indicator pairs; anything that
 * doesn't parse cleanly returns null so the raw answer shows instead.
 */
const inlineLetteredAnswer = (html: string): string | null => {
	const frags = html
		.split(/·|;|<br\s*\/?>/i)
		.map((f) => f.trim())
		.filter(Boolean);
	if (frags.length < 2) return null;

	const parsed = frags.map((f) =>
		stripTags(f).match(/^([A-Ja-j])(?:\s*[):]|\s+[–—-])\s*(.+)$/)
	);
	if (!parsed.every(Boolean)) return null;

	return parsed
		.map((m, i) => pair(m![1].toLowerCase(), bodyAfterLabel(frags[i]) ?? m![2]))
		.join(' ');
};

/**
 * ABCD answer »B) J. Robert Oppenheimer« → the same styled letter
 * indicator the pattern answers use, replacing the letter and its
 * bracket. Multi-answer questions (»A) Wigan Athletic<br />B)
 * Birmingham City«) get one indicator per lettered fragment.
 * The letter must name an option the question actually has (a–c on
 * abc questions), and a dash separator needs whitespace before it —
 * »D-Day je bio …« is an answer, not option d. Anything else passes
 * through untouched.
 */
export const abcdAnswerHtml = (
	answerHtml: string,
	abcdQuestion?: { abc_question?: boolean } | null
): string => {
	const letters = abcdQuestion?.abc_question ? 'abc' : 'abcd';

	const multi = inlineLetteredAnswer(answerHtml);
	if (multi) return multi;

	const m = stripTags(answerHtml).match(
		/^([A-Da-d])(?:\s*[):]|\s+[–—-])\s*(.+)$/
	);
	if (!m || !letters.includes(m[1].toLowerCase())) return answerHtml;
	return pair(m[1].toLowerCase(), bodyAfterLabel(answerHtml) ?? m[2]);
};

/**
 * Visual questions may carry the same a/b/c(/d) options as abcd ones —
 * their answer (»D) Maroko«) gets the identical letter-indicator
 * treatment, but only when the question actually renders options; a
 * plain visual answer that happens to open with a letter passes
 * through untouched.
 */
export const visualAnswerHtml = (
	answerHtml: string,
	visualQuestion?: { a?: string; b?: string; c?: string; d?: string } | null
): string => {
	if (!visualQuestion?.a || !visualQuestion?.b || !visualQuestion?.c)
		return answerHtml;
	return abcdAnswerHtml(answerHtml, { abc_question: !visualQuestion.d });
};

/**
 * Pattern-specific answer HTML for the glass band; falls back to the
 * question's own answer markup when the pattern has nothing custom.
 */
export const patternAnswerHtml = (
	pattern: ContentPattern | null,
	fallback: string
): string => {
	if (!pattern) return fallback;

	if (pattern.kind === 'subParts') {
		if (pattern.answerParts) {
			const parts = pattern.answerParts
				.map((p) => pair(p.label, p.html))
				.join(' ');
			if (!pattern.answerTail) return parts;
			return `${parts} ${pair(
				pattern.answerTail.label,
				pattern.answerTail.html
			)}`;
		}
		// »izdvojite trojicu« answers: a subset of letters on one line
		// (»B) X · C) Y · G) Z«) gets the same indicator treatment
		return inlineLetteredAnswer(fallback) ?? fallback;
	}
	if (pattern.kind === 'numberedList' && pattern.answerParts) {
		return pattern.answerParts.map((p) => pair(p.label, p.html)).join(' ');
	}
	if (pattern.kind === 'ordering' && pattern.answerItems) {
		// nbsp glues the arrow to its item, the plain space after it is the
		// wrap point — long sequences break after an arrow, never before
		return pattern.answerItems.join('&nbsp;<span class="arr">→</span> ');
	}
	if (pattern.kind === 'yearEvents' && pattern.yearAnswer) {
		return `<span class="yr">${pattern.yearAnswer}</span>`;
	}
	return fallback;
};
